import { useMemo, useState } from "react";

import { WidgetFrame } from "@/components/layout/WidgetFrame";
import { Chip } from "@/components/ui/Chip";
import { ComboboxMultiple } from "@/components/ui/ComboboxMultiple";
import { LinkIcon } from "@/components/ui/Icons";
import { Link } from "@/components/ui/Link";
import {
    Column,
    createChipsColumn,
    createDataColumn,
    createDataSortableColumn,
    useSort,
} from "@/components/ui/table-final-saviour/Table";
import { useSearch } from "@/components/ui/table-final-saviour/Table/hooks/useSearch";
import { ActionListTableExpandedContent } from "@/pages/DashboardPage/fms/widgets/ActionListTableWidget/ActionListTableExpandedContent";
import { EnrichedInsight } from "@/pages/DashboardPage/fms/widgets/ActionListTableWidget/action-list.types";
import {
    calculateInsightsWeight,
    getInsightSeverityColor,
    getSeverityPriority,
    getUniqueInsightsByCategoryAndType,
    insightCategoryDisplayNameMap,
    milkingPageRouteLookup,
} from "@/pages/DashboardPage/fms/widgets/ActionListTableWidget/action-list.utils";
import { routes } from "@/routes";
import { generatePath } from "@/routes/routes.utils";
import { useGetCustomerInsightsQuery } from "@/store/services/insights.service";

export const ActionListTableWidget = () => {
    const dataSource = useGetCustomerInsightsQuery();

    // Extend data with number of insights per row & severity summary
    const enrichedData = useMemo(() => {
        if (!dataSource.data) return [];

        // Initial sort by customer name
        const sortedData = dataSource.data.toSorted((a, b) => a.customerName.localeCompare(b.customerName));

        return sortedData.map((item) => {
            return {
                ...item,
                insightsWeight: calculateInsightsWeight(item.insights),
                insightCategories: item.insights.map((insight) => insight.category).join(", "),
                insightTypes: item.insights.map((insight) => insight.type).join(", "),
            };
        });
    }, [dataSource.data]);

    const { sortControls, applySort } = useSort<EnrichedInsight>({
        initialSortConfig: { key: "insightsWeight", dataType: "number", order: "desc" },
    });

    const columns = useMemo(
        () => [
            createDataSortableColumn({
                dataKey: "customerName",
                dataType: "string",
                title: "Customer Name",
                width: 200,
                ...sortControls,
                getCellValue: ({ row }) => {
                    const label = row.customerName;

                    const link = generatePath(routes.customerDetail, {
                        subRoute: routes.customerDetail.subRoutes.milking,
                        params: {
                            customerNumber: row.customerNumber,
                        },
                    });

                    if (link) {
                        return <Link to={link}>{label}</Link>;
                    }

                    return label;
                },
            }),
            createChipsColumn({
                title: "Insight Categories",
                getChips: ({ row }) => {
                    const { insights } = row;
                    // Sort unique insights alphabetically by category
                    const sortedUniqueInsights = getUniqueInsightsByCategoryAndType(insights)
                        .slice()
                        .sort((a, b) => a.category.localeCompare(b.category));

                    return sortedUniqueInsights.map((insight) => {
                        const displayName = insightCategoryDisplayNameMap[insight.category] ?? insight.category;
                        const linkTo = generatePath(routes.customerDetail, {
                            subRoute: routes.customerDetail.subRoutes.milking,
                            subSubRoute: milkingPageRouteLookup[insight.category],
                            params: {
                                customerNumber: row.customerNumber,
                            },
                        });

                        const maxSeverity = insight.insights.reduce((max, current) =>
                            getSeverityPriority(current.severity) > getSeverityPriority(max.severity) ? current : max,
                        ).severity;

                        const content = (
                            <Chip
                                key={insight.category}
                                variant={"outlined"}
                                color={getInsightSeverityColor(maxSeverity)}
                                endIcon={<LinkIcon />}
                            >
                                {displayName}
                            </Chip>
                        );

                        return linkTo ? (
                            <Link key={insight.category} to={linkTo}>
                                {content}
                            </Link>
                        ) : (
                            content
                        );
                    });
                },
                width: 400,
            }),
        ],
        [sortControls],
    ) as Column<EnrichedInsight>[];

    const searchColumns: Column<EnrichedInsight>[] = useMemo(
        () => [
            ...columns,
            createDataColumn({
                dataKey: "insightTypes",
                title: "Insight Types",
                dataType: "string",
            }),
        ],
        [columns],
    );

    const { searchValue, setSearchValue, applySearch } = useSearch({
        columns: searchColumns,
    });

    // Insight type filter
    const [selectedInsightTypes, setSelectedInsightTypes] = useState<string[]>([]);

    const searchedData = useMemo(() => {
        const filteredByInsightType =
            selectedInsightTypes.length > 0
                ? enrichedData.filter((row) =>
                      row.insights.some((insight) => selectedInsightTypes.includes(insight.type)),
                  )
                : enrichedData;

        return applySort(applySearch(filteredByInsightType));
    }, [enrichedData, applySearch, applySort, selectedInsightTypes]);

    const { insightTypes, insightTypeDisplayNameMap } = useMemo(() => {
        if (!dataSource.data) return { insightTypes: [], insightTypeDisplayNameMap: new Map<string, string>() };

        // Extract unique insight types and their display names from the data
        const typeDisplayNameLookup = new Map<string, string>();
        for (const item of dataSource.data) {
            for (const insight of item.insights) {
                if (!typeDisplayNameLookup.has(insight.type)) {
                    typeDisplayNameLookup.set(insight.type, (insight.data.displayName as string) ?? insight.type); // TODO: Remove type cast after #1896 LSSA-7350
                }
            }
        }

        const types = [...typeDisplayNameLookup.keys()].sort((a, b) =>
            (typeDisplayNameLookup.get(a) ?? a).localeCompare(typeDisplayNameLookup.get(b) ?? b),
        );

        return { insightTypes: types, insightTypeDisplayNameMap: typeDisplayNameLookup };
    }, [dataSource.data]);

    return (
        <>
            <WidgetFrame.Title title={"Action List"} icon={"lely-icon-show"}>
                <WidgetFrame.Title.Search value={searchValue} setValue={setSearchValue} />
                <WidgetFrame.Title.Actions>
                    <WidgetFrame.Title.Select minWidth={250}>
                        <ComboboxMultiple<string, string>
                            isLoading={dataSource.isLoading}
                            value={selectedInsightTypes}
                            options={insightTypes}
                            onChange={setSelectedInsightTypes}
                            // @ts-expect-error FIXME WDS component incorrectly uses any here
                            getOptionKey={(item) => item}
                            getOptionLabel={(item: string) => insightTypeDisplayNameMap.get(item) ?? item}
                            // @ts-expect-error FIXME WDS component incorrectly uses any here
                            getOptionValue={(item) => item}
                            placeholder={"Select Insight Type..."}
                            showClearButton
                            limitMaxValueOptions={2}
                        />
                    </WidgetFrame.Title.Select>
                </WidgetFrame.Title.Actions>
                <WidgetFrame.Title.Information pageId={2145779720} />
            </WidgetFrame.Title>
            <WidgetFrame.TableExpandable
                ExpandedContent={(row) => <ActionListTableExpandedContent {...row} />}
                columns={columns}
                getRowId={(row) => row.customerNumber}
                dataSource={{ ...dataSource, data: searchedData }}
                maxHeight={600}
            />
        </>
    );
};
